import {
	FrameName,
	tilerepoBigFishingBoatsFrameNames,
	tilerepoBlastFurnacesFrameNames,
	tilerepoBuildingsFrameNames,
	tilerepoFarmFieldsFrameNames,
	tilerepoFishingBoatsFrameNames,
	tilerepoFishTrapsFrameNames,
	tilerepoLightHousesFrameNames,
	tilerepoLookoutTowersFrameNames,
	tilerepoSettlementCastleBuildingsFrameNames,
} from "@/game/entities/config";
import useTileRepository from "@/hooks/useTileRepository";
import GameError, { GameErrorType } from "@/libs/GameError";
import { type Timestamp } from "@/types";
import {
	createImmutableState,
	getImmutableState,
	getImmutableStateProperty,
	setImmutableState,
	setImmutableStateProperty,
} from "@/utils/state";
import { defineStore } from "pinia";
import { type ComputedRef } from "vue";

export type BuildingsStorageStoreState = Record<FrameName, number>;

type BuildingsStorageStoreDiffAction = {
	type: "set" | "increase" | "decrease";
	key: FrameName;
	currentValue: number;
	value: number;
	reason?: string | false;
	timestamp: Timestamp;
};

type BuildingsStorageStorePayload = {
	state: BuildingsStorageStoreState;
	actions: Array<BuildingsStorageStoreDiffAction>;
};

type BuildingsStorageStoreStatePatch =
	| Partial<Record<FrameName, number>>
	| {
			state?: Partial<Record<FrameName, number>>;
	  }
	| null
	| undefined;

type BuildingsStorageStoreWrappedStatePatch = {
	state?: Partial<Record<FrameName, number>>;
};

export type BuildingsStorageStoreGetters = Record<
	FrameName,
	ComputedRef<number>
>;

type ValidateBuildingsStorageAmountProps = {
	ignoreBoatLimit?: boolean;
};

type BuildingsStorageStore = BuildingsStorageStoreGetters & {
	$getPayload: () => BuildingsStorageStorePayload | null;
	$resetPayload: () => void;
	$setState: (value: BuildingsStorageStoreStatePatch) => void;
	getAllBuildings: () => BuildingsStorageStoreState;
	set: (key: FrameName, value: number, reason?: string | false) => void;
	increase: (key: FrameName, value: number, reason?: string | false) => void;
	decrease: (key: FrameName, value: number, reason?: string | false) => void;
	validateBuildingsStorageAmount: (
		key: FrameName,
		options?: ValidateBuildingsStorageAmountProps,
	) => boolean;
};

const normalizeStatePatch = (
	value: BuildingsStorageStoreStatePatch,
): Partial<Record<FrameName, number>> | null => {
	if (!value) return null;
	if (typeof value === "object" && "state" in value) {
		return (value as BuildingsStorageStoreWrappedStatePatch).state ?? null;
	}
	return value as Partial<Record<FrameName, number>>;
};

const STORE_NAME = "storage/buildings";

const CONSOLE_COLOR = [
	"%c STORE ACTION ",
	"background-color:indianred;color:white;font-weight:700;",
];

export default defineStore(STORE_NAME, (): BuildingsStorageStore => {
	const keys = (
		Array.from(tilerepoBuildingsFrameNames) as Array<FrameName>
	).filter(
		(frameName: FrameName): boolean =>
			!tilerepoSettlementCastleBuildingsFrameNames.has(frameName),
	);

	const state = createImmutableState<BuildingsStorageStoreState>(
		keys.reduce(
			(
				acc: BuildingsStorageStoreState,
				key: FrameName,
			): BuildingsStorageStoreState => ({
				...acc,
				[key]: 0,
			}),
			{} as BuildingsStorageStoreState,
		),
	);
	const validKeys = new Set<FrameName>(keys);

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getState = (): BuildingsStorageStoreState => {
		const data = getImmutableState(state).value;
		const keys = Object.keys(data) as Array<FrameName>;
		return keys.reduce(
			(
				acc: BuildingsStorageStoreState,
				key: FrameName,
			): BuildingsStorageStoreState => ({
				...acc,
				[key]: data[key],
			}),
			{} as BuildingsStorageStoreState,
		);
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $actions = new Array<BuildingsStorageStoreDiffAction>();

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getActions = (): Array<BuildingsStorageStoreDiffAction> =>
		$actions.splice(0, $actions.length);

	// Used only for save.
	// DO NOT USE IN GAME!
	let $isPatched = false;

	// Used only for save.
	// DO NOT USE IN GAME!
	let $payloadInProgress: BuildingsStorageStorePayload | null = null;

	// Used only for save.
	// DO NOT USE IN GAME!
	const $markPatched = (): void => {
		$isPatched = true;
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $getPayload = (): BuildingsStorageStorePayload | null => {
		if (!$isPatched && $actions.length === 0) return null;
		if ($payloadInProgress !== null) return $payloadInProgress;

		return ($payloadInProgress = {
			state: $getState(),
			actions: $getActions(),
		});
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $resetPayload = (): void => {
		$payloadInProgress = null;
	};

	// Used only for save.
	// DO NOT USE IN GAME!
	const $setState = (value: BuildingsStorageStoreStatePatch): void => {
		const rawState = normalizeStatePatch(value);
		if (!rawState) return;

		const nextState: BuildingsStorageStoreState = { ...state.value };

		for (const [frameName, rawValue] of Object.entries(rawState) as Array<
			[FrameName, number | undefined]
		>) {
			if (!validKeys.has(frameName)) continue;
			if (rawValue === undefined || !Number.isFinite(rawValue)) continue;

			nextState[frameName] = rawValue;
		}

		for (const frameName of tilerepoFarmFieldsFrameNames) {
			if (!(frameName in nextState)) continue;
			delete nextState[frameName];
		}

		setImmutableState(state, nextState);
		$markPatched();
	};

	const getters = keys.reduce(
		(
			acc: BuildingsStorageStoreGetters,
			key: FrameName,
		): BuildingsStorageStoreGetters => ({
			...acc,
			[key]: getImmutableStateProperty(state, key, 0),
		}),
		{} as BuildingsStorageStoreGetters,
	);

	const getAllBuildings = (): BuildingsStorageStoreState => state.value;

	const set = (
		key: FrameName,
		value: number,
		reason?: string | false,
	): void => {
		if (isNaN(value))
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Invalid value for frame name ${key}: ${value}`,
			);

		const currentValue = state.value[key] ?? 0;
		if (currentValue === value) return;

		if (value < 0)
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Cannot set ${key} below zero!`,
			);

		setImmutableStateProperty(state, key, value);
		$markPatched();

		if (reason !== false)
			$actions.push({
				type: "set",
				key,
				currentValue,
				value,
				reason,
				timestamp: Date.now(),
			});

		if (reason === undefined) {
			// eslint-disable-next-line no-console
			console.trace(
				...CONSOLE_COLOR,
				"[BuildingsStorageStore] TREBA DOPLNIT REASON",
			);
		}
	};

	const increase = (
		key: FrameName,
		value: number,
		reason?: string | false,
	): void => {
		if (isNaN(value))
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Invalid value for frame name ${key}: ${value}`,
			);

		const currentValue = state.value[key] ?? 0;
		if (value === 0) return;

		setImmutableStateProperty(state, key, (state.value[key] ?? 0) + value);
		$markPatched();

		if (reason !== false)
			$actions.push({
				type: "increase",
				key,
				currentValue,
				value,
				reason,
				timestamp: Date.now(),
			});

		if (reason === undefined) {
			// eslint-disable-next-line no-console
			console.trace(
				...CONSOLE_COLOR,
				"[BuildingsStorageStore] TREBA DOPLNIT REASON",
			);
		}
	};

	const decrease = (
		key: FrameName,
		value: number,
		reason?: string | false,
	): void => {
		if (isNaN(value))
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Invalid value for frame name ${key}: ${value}`,
			);

		const currentValue = state.value[key] ?? 0;
		if (value === 0) return;

		const targetValue = currentValue - value;
		if (targetValue < 0)
			throw new GameError(
				GameErrorType.RESOURCE_ERROR,
				`Cannot decrease ${key} below zero!`,
			);

		setImmutableStateProperty(state, key, targetValue);
		$markPatched();

		if (reason !== false)
			$actions.push({
				type: "decrease",
				key,
				currentValue,
				value,
				reason,
				timestamp: Date.now(),
			});

		if (reason === undefined) {
			// eslint-disable-next-line no-console
			console.trace(
				...CONSOLE_COLOR,
				"[BuildingsStorageStore] TREBA DOPLNIT REASON",
			);
		}
	};

	// TODO: pouzit pri stavani z inventara
	const validateBuildingsStorageAmount = (
		key: FrameName,
		options?: ValidateBuildingsStorageAmountProps,
	): boolean => {
		if (tilerepoFishingBoatsFrameNames.has(key)) {
			const fishingBoatsInStorage =
				(state.value[FrameName.SHALLOW_SEA_FISHING_BOAT] ?? 0) +
				(state.value[FrameName.DEEP_SEA_FISHING_BOAT] ?? 0);
			return (
				fishingBoatsInStorage > 0 &&
				(!options?.ignoreBoatLimit
					? (useTileRepository().getAShipyardEntity()
							?.availableFishingBoats ?? 0) > 0
					: true)
			);
		}

		if (tilerepoBigFishingBoatsFrameNames.has(key)) {
			const bigFishingBoatsInStorage =
				(state.value[FrameName.SHALLOW_SEA_BIG_FISHING_BOAT] ?? 0) +
				(state.value[FrameName.DEEP_SEA_BIG_FISHING_BOAT] ?? 0);
			return (
				bigFishingBoatsInStorage > 0 &&
				(!options?.ignoreBoatLimit
					? (useTileRepository().getAShipyardEntity()
							?.availableFishingBoats ?? 0) > 0
					: true)
			);
		}

		if (tilerepoFishTrapsFrameNames.has(key)) {
			const fishTrapsInStorage =
				(state.value[FrameName.SHALLOW_SEA_SMALL_FISH_TRAP] ?? 0) +
				(state.value[FrameName.SHALLOW_SEA_MEDIUM_FISH_TRAP] ?? 0);
			return fishTrapsInStorage > 0;
		}

		if (tilerepoLightHousesFrameNames.has(key)) {
			const lighthousesInStorage =
				(state.value[FrameName.LIGHTHOUSE] ?? 0) +
				(state.value[FrameName.SHALLOW_SEA_ROCKS_LIGHTHOUSE] ?? 0);
			return lighthousesInStorage > 0;
		}

		if (tilerepoBlastFurnacesFrameNames.has(key)) {
			const blastFurnacesInStorage =
				(state.value[FrameName.HILLS_BLAST_FURNACE] ?? 0) +
				(state.value[FrameName.MOUNTAINS_BLAST_FURNACE] ?? 0);
			return blastFurnacesInStorage > 0;
		}

		if (tilerepoLookoutTowersFrameNames.has(key)) {
			const lookoutTowersInStorage =
				(state.value[FrameName.HILLS_LOOKOUT_TOWER] ?? 0) +
				(state.value[FrameName.MOUNTAINS_LOOKOUT_TOWER] ?? 0);
			return lookoutTowersInStorage > 0;
		}

		// Aktualne sa neda dat do storage.
		// if (tileRepoScoutTowerFrameNames.has(key)) {
		// 	const scoutTowersInStorage =
		// 		(state.value[FrameName.HILLS_SCOUT_TOWER] ?? 0) +
		// 		(state.value[FrameName.MOUNTAINS_SCOUT_TOWER] ?? 0);
		// 	return scoutTowersInStorage > 0;
		// }

		const storedValue = state.value[key] ?? 0;
		return storedValue > 0;
	};

	return {
		$getPayload,
		$resetPayload,
		$setState,
		...getters,
		getAllBuildings,
		set,
		increase,
		decrease,
		validateBuildingsStorageAmount,
	};
});
